import { format } from "date-fns";

export interface ExportColumn {
  key: string;
  header: string;
  format?: (val: any, row: any) => string;
}

export type EntityType =
  | "projects"
  | "invoices"
  | "bills"
  | "vendors"
  | "customers"
  | "expenses"
  | "approvals"
  | "change_orders"
  | "work_orders";

const fmtCurrency = (val: any) => {
  const n = Number(val);
  if (isNaN(n)) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

const fmtDate = (val: any) => {
  if (!val) return "";
  try { return format(new Date(val), "MMM d, yyyy"); } catch { return String(val); }
};

// ─── Column definitions per entity ──────────────────────────────────────

const PROJECT_COLUMNS: ExportColumn[] = [
  { key: "project_number", header: "Project #" },
  { key: "name", header: "Project Name" },
  { key: "status", header: "Status" },
  { key: "customer_name", header: "Customer", format: (_, row) => row.customers?.name ?? "" },
  { key: "city", header: "City" },
  { key: "state", header: "State" },
  { key: "created_at", header: "Created", format: (v) => fmtDate(v) },
];

const INVOICE_COLUMNS: ExportColumn[] = [
  { key: "invoice_number", header: "Invoice #" },
  { key: "project_name", header: "Project", format: (_, row) => row.projects?.name ?? "" },
  { key: "invoice_date", header: "Date", format: (v) => fmtDate(v) },
  { key: "total", header: "Total", format: (v) => fmtCurrency(v) },
  { key: "amount_paid", header: "Paid", format: (v) => fmtCurrency(v) },
  { key: "status", header: "Status" },
];

const INVOICE_CUSTOMER_COLUMNS: ExportColumn[] = [
  { key: "invoice_number", header: "Invoice #" },
  { key: "invoice_date", header: "Date", format: (v) => fmtDate(v) },
  { key: "due_date", header: "Due Date", format: (v) => fmtDate(v) },
  { key: "total", header: "Amount", format: (v) => fmtCurrency(v) },
  { key: "amount_paid", header: "Paid", format: (v) => fmtCurrency(v) },
  { key: "status", header: "Status" },
];

const BILL_COLUMNS: ExportColumn[] = [
  { key: "bill_number", header: "Bill #" },
  { key: "project_name", header: "Project", format: (_, row) => row.projects?.name ?? "" },
  { key: "vendor_name", header: "Vendor", format: (_, row) => row.vendors?.name ?? "" },
  { key: "bill_date", header: "Date", format: (v) => fmtDate(v) },
  { key: "subtotal", header: "Subtotal", format: (v) => fmtCurrency(v) },
  { key: "retainage_amount", header: "Retainage", format: (v) => fmtCurrency(v) },
  { key: "total", header: "Total", format: (v) => fmtCurrency(v) },
  { key: "status", header: "Status" },
];

const VENDOR_COLUMNS: ExportColumn[] = [
  { key: "name", header: "Name" },
  { key: "vendor_type", header: "Type" },
  { key: "contact_name", header: "Contact" },
  { key: "email", header: "Email" },
  { key: "phone", header: "Phone" },
  { key: "payment_terms", header: "Payment Terms" },
  { key: "created_at", header: "Created", format: (v) => fmtDate(v) },
];

const CUSTOMER_COLUMNS: ExportColumn[] = [
  { key: "name", header: "Name" },
  { key: "customer_type", header: "Type" },
  { key: "contact_name", header: "Contact" },
  { key: "email", header: "Email" },
  { key: "phone", header: "Phone" },
  { key: "created_at", header: "Created", format: (v) => fmtDate(v) },
];

const EXPENSE_COLUMNS: ExportColumn[] = [
  { key: "expense_date", header: "Date", format: (v) => fmtDate(v) },
  { key: "vendor_display", header: "Vendor / Payee", format: (_, row) => row.vendors?.name ?? row.vendor_name ?? "" },
  { key: "category", header: "Category" },
  { key: "description", header: "Description" },
  { key: "amount", header: "Amount", format: (v) => fmtCurrency(v) },
  { key: "payment_method", header: "Payment Method" },
  { key: "project_name", header: "Project", format: (_, row) => row.projects?.name ?? "" },
  { key: "status", header: "Status" },
];

const APPROVAL_COLUMNS: ExportColumn[] = [
  { key: "title", header: "Title" },
  { key: "request_type", header: "Type" },
  { key: "project_name", header: "Project", format: (_, row) => row.projects?.name ?? "" },
  { key: "status", header: "Status" },
  { key: "urgency", header: "Urgency" },
  { key: "created_at", header: "Requested", format: (v) => fmtDate(v) },
];

const CHANGE_ORDER_COLUMNS: ExportColumn[] = [
  { key: "number", header: "CO #" },
  { key: "description", header: "Description" },
  { key: "amount", header: "Amount", format: (v) => fmtCurrency(v) },
  { key: "status", header: "Status" },
  { key: "created_at", header: "Created", format: (v) => fmtDate(v) },
];

const WORK_ORDER_COLUMNS: ExportColumn[] = [
  { key: "number", header: "WO #" },
  { key: "description", header: "Description" },
  { key: "vendor_name", header: "Contractor", format: (_, row) => row.vendors?.name ?? row.contractor_name ?? "" },
  { key: "total", header: "Total", format: (v) => fmtCurrency(v) },
  { key: "status", header: "Status" },
  { key: "created_at", header: "Created", format: (v) => fmtDate(v) },
];

// ─── Registry ───────────────────────────────────────────────────────────

const COLUMN_REGISTRY: Record<EntityType, ExportColumn[]> = {
  projects: PROJECT_COLUMNS,
  invoices: INVOICE_COLUMNS,
  bills: BILL_COLUMNS,
  vendors: VENDOR_COLUMNS,
  customers: CUSTOMER_COLUMNS,
  expenses: EXPENSE_COLUMNS,
  approvals: APPROVAL_COLUMNS,
  change_orders: CHANGE_ORDER_COLUMNS,
  work_orders: WORK_ORDER_COLUMNS,
};

const CUSTOMER_SAFE_REGISTRY: Partial<Record<EntityType, ExportColumn[]>> = {
  invoices: INVOICE_CUSTOMER_COLUMNS,
};

export function getExportColumns(
  entityType: EntityType,
  options?: { isCustomerPortal?: boolean }
): ExportColumn[] {
  if (options?.isCustomerPortal && CUSTOMER_SAFE_REGISTRY[entityType]) {
    return CUSTOMER_SAFE_REGISTRY[entityType]!;
  }
  return COLUMN_REGISTRY[entityType] ?? [];
}

// File name generator
export function getExportFileName(entityType: EntityType, suffix?: string): string {
  const date = format(new Date(), "yyyy-MM-dd");
  const base = entityType.replace(/_/g, "-");
  return suffix ? `${base}-${suffix}-${date}` : `${base}-${date}`;
}
